import { getBoardsMenu } from 'selectors';
import { StandardThunkAction } from 'types';
import {
  BoardsMenuSelectedItemModel,
  ReopenBoardPopoverModel,
} from 'types/models';
import { Action, actionCreator } from 'util/actions';
import {
  getSidebarValues,
  setSidebarValues,
} from 'util/application-storage/sidebar';
import { createReducer } from 'util/reducers';

import {
  LOAD_MY_BOARDS_ERROR,
  LOAD_MY_BOARDS_REQUEST,
  LOAD_MY_BOARDS_SUCCESS,
} from 'modules/loaders';

import { SET_BOARD_CLOSED } from 'modules/state/models/boards';
import {
  CREATE_BOARD_SUBMIT_DONE,
  CreateBoardSubmitDoneAction,
} from 'modules/state/ui/create-menu';

type IdTeam = string | null;

// Action types
const ADD_BOARD_TO_RECENT = Symbol('ADD_BOARD_TO_RECENT');
const REMOVE_BOARD_FROM_RECENT = Symbol('REMOVE_BOARD_FROM_RECENT');
const SET_BOARDS_MENU_SCROLL_POSITION = Symbol(
  'SET_BOARDS_MENU_SCROLL_POSITION',
);
const SHOW_LESS_ACTIVE_BOARDS = Symbol('SHOW_LESS_ACTIVE_BOARDS');
const TOGGLE_ALWAYS_OPEN = Symbol('TOGGLE_ALWAYS_OPEN');
const TOGGLE_BOARDS_MENU = Symbol('TOGGLE_BOARDS_MENU');
const UPDATE_SIDEBAR_STATE = Symbol('UPDATE_SIDEBAR_STATE');
const TOGGLE_DELETING_BOARD = Symbol('TOGGLE_DELETING_BOARD');
const SET_BOARDS_MENU_SEARCH_TEXT = Symbol('SET_BOARDS_MENU_SEARCH_TEXT');
const SET_SELECTED_BOARD = Symbol('SET_SELECTED_BOARD');
const SET_REOPEN_BOARD_POPOVER_DATA = Symbol('SET_REOPEN_BOARD_POPOVER_DATA');

type AddBoardToRecent = Action<typeof ADD_BOARD_TO_RECENT, { id: string }>;
type RemoveBoardFromRecent = Action<
  typeof REMOVE_BOARD_FROM_RECENT,
  { id: string }
>;
type ShowLessActiveBoardsAction = Action<
  typeof SHOW_LESS_ACTIVE_BOARDS,
  IdTeam
>;
type SetBoardsMenuScrollPositionAction = Action<
  typeof SET_BOARDS_MENU_SCROLL_POSITION,
  number
>;
type ToggleAlwaysOpenAction = Action<typeof TOGGLE_ALWAYS_OPEN, null>;
type ToggleBoardsMenuAction = Action<typeof TOGGLE_BOARDS_MENU, null>;
type UpdateSidebarStateAction = Action<typeof UPDATE_SIDEBAR_STATE, null>;
type ToggleDeletingBoardAction = Action<typeof TOGGLE_DELETING_BOARD, string>;
type SetBoardsMenuSearchText = Action<
  typeof SET_BOARDS_MENU_SEARCH_TEXT,
  string
>;
type SetSelectedBoardAction = Action<
  typeof SET_SELECTED_BOARD,
  BoardsMenuSelectedItemModel | null
>;
type SetReopenBoardPopoverData = Action<
  typeof SET_REOPEN_BOARD_POPOVER_DATA,
  ReopenBoardPopoverModel
>;

// Actions Creators
export const toggleAlwaysOpenAction = actionCreator<ToggleAlwaysOpenAction>(
  TOGGLE_ALWAYS_OPEN,
);
export const toggleBoardsMenuAction = actionCreator<ToggleBoardsMenuAction>(
  TOGGLE_BOARDS_MENU,
);
export const updateSidebarState = actionCreator<UpdateSidebarStateAction>(
  UPDATE_SIDEBAR_STATE,
);
export const setReopenBoardPopoverData = actionCreator<
  SetReopenBoardPopoverData
>(SET_REOPEN_BOARD_POPOVER_DATA);
export const setBoardsMenuSelectedBoard = (
  selected: BoardsMenuSelectedItemModel | null,
) => actionCreator<SetSelectedBoardAction>(SET_SELECTED_BOARD)(selected);
export const toggleDeletingBoard = actionCreator<ToggleDeletingBoardAction>(
  TOGGLE_DELETING_BOARD,
);
export const setBoardsMenuScrollPosition = actionCreator<
  SetBoardsMenuScrollPositionAction
>(SET_BOARDS_MENU_SCROLL_POSITION);
export const setBoardsMenuSearchText = actionCreator<SetBoardsMenuSearchText>(
  SET_BOARDS_MENU_SEARCH_TEXT,
);
export const addBoardToRecent = actionCreator<AddBoardToRecent>(
  ADD_BOARD_TO_RECENT,
);
export const removeBoardFromRecent = actionCreator<RemoveBoardFromRecent>(
  REMOVE_BOARD_FROM_RECENT,
);
export const showLessActiveBoards = (idTeam: IdTeam) =>
  actionCreator<ShowLessActiveBoardsAction>(SHOW_LESS_ACTIVE_BOARDS)(idTeam);

// Reducer
export interface BoardsMenuState {
  alwaysOpenBoardsMenu: boolean;
  showBoardsListSidebar: boolean;
  isDeletingBoard: {
    [key: string]: boolean;
  };
  teamsShowingLessActiveBoards: IdTeam[];
  loading: boolean;
  selectedBoard: BoardsMenuSelectedItemModel | null;
  idRecentBoards: string[];
  scrollPosition: number;
  searchText: string;
  reopenBoardPopover: ReopenBoardPopoverModel | null;
}

const sidebar = getSidebarValues();
const initialState: BoardsMenuState = {
  alwaysOpenBoardsMenu: !!sidebar && !!sidebar.pinBoardsListSidebar,
  showBoardsListSidebar: !!sidebar && !!sidebar.showBoardsListSidebar,
  isDeletingBoard: {},
  teamsShowingLessActiveBoards: [],
  loading: false,
  selectedBoard: null,
  idRecentBoards: (sidebar && sidebar.idRecentBoards) || [],
  scrollPosition: 0,
  searchText: '',
  reopenBoardPopover: null,
};

export default createReducer(initialState, {
  [CREATE_BOARD_SUBMIT_DONE](state, { payload }: CreateBoardSubmitDoneAction) {
    return {
      ...state,
      idRecentBoards: payload
        ? [
            payload.id,
            ...state.idRecentBoards.filter((idBoard) => idBoard !== payload.id),
          ].slice(0, 16)
        : state.idRecentBoards,
    };
  },
  [SHOW_LESS_ACTIVE_BOARDS](
    state: BoardsMenuState,
    { payload }: ShowLessActiveBoardsAction,
  ) {
    return {
      ...state,
      teamsShowingLessActiveBoards: [
        ...state.teamsShowingLessActiveBoards,
        payload,
      ],
    };
  },

  [LOAD_MY_BOARDS_REQUEST](state) {
    return {
      ...state,
      loading: true,
    };
  },

  [LOAD_MY_BOARDS_SUCCESS](state) {
    return {
      ...state,
      loading: false,
    };
  },

  [LOAD_MY_BOARDS_ERROR](state) {
    return {
      ...state,
      loading: false,
    };
  },

  [ADD_BOARD_TO_RECENT](state, { payload }: AddBoardToRecent) {
    return {
      ...state,
      // Follow the same rules as sidebar-state.coffee:
      // - Filter the board out of the recents list (in case it already exists)
      // - unshift the board to the beginning of the list
      // - clamp to a max size of 16
      idRecentBoards: [
        payload.id,
        ...state.idRecentBoards.filter((idBoard) => idBoard !== payload.id),
      ].slice(0, 16),
    };
  },

  [REMOVE_BOARD_FROM_RECENT](state, { payload }: RemoveBoardFromRecent) {
    return {
      ...state,
      idRecentBoards: state.idRecentBoards.filter(
        (currID) => currID !== payload.id,
      ),
    };
  },

  [SET_BOARDS_MENU_SCROLL_POSITION](
    state,
    { payload: scrollPosition }: SetBoardsMenuScrollPositionAction,
  ) {
    return {
      ...state,
      scrollPosition,
    };
  },

  [TOGGLE_ALWAYS_OPEN](state) {
    return {
      ...state,
      alwaysOpenBoardsMenu: !state.alwaysOpenBoardsMenu,
      selectedBoard: null,
    };
  },

  [TOGGLE_BOARDS_MENU](state) {
    return {
      ...state,
      showBoardsListSidebar: !state.showBoardsListSidebar,
    };
  },

  [TOGGLE_DELETING_BOARD](
    state,
    { payload: idBoard }: ToggleDeletingBoardAction,
  ) {
    return {
      ...state,
      isDeletingBoard: {
        ...state.isDeletingBoard,
        [idBoard]: !state.isDeletingBoard[idBoard],
      },
    };
  },

  [SET_BOARDS_MENU_SEARCH_TEXT](state, { payload }: SetBoardsMenuSearchText) {
    return {
      ...state,
      searchText: payload || '',
      selectedBoard: null,
    };
  },

  [UPDATE_SIDEBAR_STATE](state) {
    const localState = getSidebarValues();

    return {
      ...state,
      alwaysOpenBoardsMenu: !!localState && !!localState.pinBoardsListSidebar,
      idRecentBoards: (localState && localState.idRecentBoards) || [],
      showBoardsListSidebar: !!localState && !!localState.showBoardsListSidebar,
    };
  },

  [SET_SELECTED_BOARD](state, { payload }: SetSelectedBoardAction) {
    return {
      ...state,
      selectedBoard: payload,
    };
  },

  [SET_REOPEN_BOARD_POPOVER_DATA](
    state,
    { payload }: SetReopenBoardPopoverData,
  ) {
    return {
      ...state,
      reopenBoardPopover: payload,
    };
  },

  [SET_BOARD_CLOSED](state) {
    return {
      ...state,
      reopenBoardPopover: null,
    };
  },
});

// Side effects
const updateLocalStorage = (state: BoardsMenuState) => {
  setSidebarValues({
    ...getSidebarValues(),
    pinBoardsListSidebar: state.alwaysOpenBoardsMenu,
    idRecentBoards: state.idRecentBoards,
    showBoardsListSidebar: state.showBoardsListSidebar,
  });
};

export const toggleBoardsMenu = (): StandardThunkAction => {
  return async (dispatch, getState) => {
    dispatch(toggleBoardsMenuAction());

    updateLocalStorage(getBoardsMenu(getState()));
  };
};

export const toggleAlwaysOpenBoardsMenu = (): StandardThunkAction => {
  return async (dispatch, getState) => {
    dispatch(toggleAlwaysOpenAction());

    updateLocalStorage(getBoardsMenu(getState()));
  };
};

export const removeRecentBoardByID = (
  id: string,
): StandardThunkAction<void> => {
  return async (dispatch, getState) => {
    dispatch(removeBoardFromRecent({ id }));
    updateLocalStorage(getBoardsMenu(getState()));
  };
};



// WEBPACK FOOTER //
// ./app/gamma/src/modules/state/ui/boards-menu.ts